// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, inject, model, ModelSignal, signal, Signal, WritableSignal } from '@angular/core';
import { OverviewComponent } from "@shared/components/templates/overview/overview.component";
import { OverviewHeaderComponent } from "@shared/components/overview-header/overview-header.component";
import { OverviewHeaderTitleComponent } from "@shared/components/overview-header/components/overview-header-title/overview-header-title.component";
import { OverviewHeaderAddComponent } from "@shared/components/overview-header/components/overview-header-add/overview-header-add.component";
import { OverviewHeaderOperatorsComponent } from "@shared/components/overview-header/components/overview-header-operators/overview-header-operators.component";
import { OverviewHeaderRefreshComponent } from "@shared/components/overview-header/components/overview-header-operators/components/overview-header-refresh/overview-header-refresh.component";
import { OverviewHeaderSettingsComponent } from "@shared/components/overview-header/components/overview-header-operators/components/overview-header-settings/overview-header-settings.component";
import { provideTableConfigService } from '@core/services/table/tokens/table-scope.token';
import { PROJECT_SPECIAL_TIMES_TABLE_CONFIG } from './consts/project-documents-table-default.config';
import { TableConfigService } from '@core/services/table/table-config.service';
import { TableComponent } from "@shared/components/table/table.component";
import { TableHeaderGroupComponent } from "@shared/components/table/components/table-header-group/table-header-group.component";
import { TableHeaderRowComponent } from "@shared/components/table/components/table-header-row/table-header-row.component";
import { TableHeaderComponent } from "@shared/components/table/components/table-header/table-header.component";
import { BclTableHeaderComponent } from "@shared/components/table-implementation/bcl-table-header/bcl-table-header.component";
import { TableDataGroupComponent } from "@shared/components/table/components/table-data-group/table-data-group.component";
import { InfiniteScrollDirective } from "@shared/directives/infinite-scroll/infinite-scroll.directive";
import { VirtualScrollWrapperComponent } from '@shared/components/virtual-scroll-wrapper/virtual-scroll-wrapper.component';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { parseToProjectCustomerDocument, PROJECT_CUSTOMER_DOCUMENT_SPECIAL_DURATIONS, ProjectCustomerDocument, ProjectCustomerDocumentDto, ProjectCustomerDocumentId } from './types/project-customer-document.type';
import { TableDataRowComponent } from "@shared/components/table/components/table-data-row/table-data-row.component";
import { OgsItemDirective } from "@shared/directives/ogs-item.directive";
import { TableDataComponent } from "@shared/components/table/components/table-data/table-data.component";
import { BclTableDataComponent } from "@shared/components/table-implementation/bcl-table-data/bcl-table-data.component";
import { toSignal } from '@angular/core/rxjs-interop';
import { TableSkeletonComponent } from "@shared/components/table/components/table-skeleton/table-skeleton.component";
import { PROJECT_NUMBER } from '@features/project/constants/routes.constant';
import { ProjectDocumentsMenuComponent } from './components/project-documents-menu/project-documents-menu.component';
import { parseToTableData, TableData } from '@shared/components/table-implementation/bcl-table-data/types/table-data.type';
import { TableColumn } from '@shared/components/table-implementation/types/column/header.type';
import { DATA_DISPLAY_TYPES, DataDisplayType } from '@shared/components/table-implementation/bcl-table-data/data-display-type';
import { EntityState, TableDataService } from '@core/services/table/table-data.service';
import { TABLE_DATA_API_CONFIG_TYPES } from '@core/services/table/types/table-api-config';
import { map } from 'rxjs';
import { ProjectCustomerDocumentService } from './services/project-customer.service';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { TableColumnMenuComponent } from "@shared/components/table-implementation/bcl-table-header/components/table-column-menu/table-column-menu.component";
import { TableColumnMenuItemGroupComponent } from "@shared/components/table-implementation/bcl-table-header/components/table-column-menu/components/table-column-menu-item-group/table-column-menu-item-group.component";
import { TableColumnMenuItemChangeVisibilityComponent } from "@shared/components/table-implementation/bcl-table-header/components/table-column-menu/components/table-column-menu-item-change-visibility/table-column-menu-item-change-visibility.component";
import { OverviewHeaderColumnChipsComponent } from "@shared/components/overview-header/components/overview-header-column-chips/overview-header-column-chips.component";
import { OverviewHeaderFilterChipsComponent } from "@shared/components/overview-header/components/overview-header-filter-chips/overview-header-filter-chips.component";
import { OverviewHeaderSearchComponent } from "@shared/components/overview-header/components/overview-header-search/overview-header-search.component";


const TRANSLATION_SCOPE: string = "feature/project-customer-documents-overview";

// eslint-disable-next-line @tseslint/no-unnecessary-type-parameters
function getDataForField<T extends Record<string, unknown>> (item: T, header: TableColumn): TableData | undefined {
  const key: string = header.field;
  const dataDisplayType: DataDisplayType = header.dataDisplayType;
  const value: unknown = item[ key ];

  if (dataDisplayType === DATA_DISPLAY_TYPES.duration) {
    if (value === PROJECT_CUSTOMER_DOCUMENT_SPECIAL_DURATIONS.projectEnd) {
      return {
        type: DATA_DISPLAY_TYPES.localizedString,
        translationInfo: {
          defaultText: "Bis Projektende",
          token: "label-duration-until-project-end",
          scope: TRANSLATION_SCOPE
        }
      };
    }

    if (value === PROJECT_CUSTOMER_DOCUMENT_SPECIAL_DURATIONS.unlimited) {
      return {
        type: DATA_DISPLAY_TYPES.localizedString,
        translationInfo: {
          defaultText: "Unbegrenzt",
          token: "label-duration-unlimited",
          scope: TRANSLATION_SCOPE
        }
      };
    }
  }

  return parseToTableData(dataDisplayType, value);
}

@Component({
  selector: 'app-project-documents',
  standalone: true,
  imports: [ OverviewComponent,
    OverviewHeaderComponent,
    OverviewHeaderTitleComponent,
    OverviewHeaderAddComponent,
    OverviewHeaderOperatorsComponent,
    OverviewHeaderRefreshComponent,
    OverviewHeaderSettingsComponent,
    TableComponent,
    TableHeaderGroupComponent,
    TableHeaderRowComponent,
    TableHeaderComponent,
    BclTableHeaderComponent,
    TableDataGroupComponent,
    VirtualScrollWrapperComponent,
    InfiniteScrollDirective,
    TableDataRowComponent,
    OgsItemDirective,
    TableDataComponent,
    BclTableDataComponent,
    TableSkeletonComponent,
    ProjectDocumentsMenuComponent,
    TranslationPipeModule,
    TableColumnMenuComponent,
    TableColumnMenuItemGroupComponent,
    TableColumnMenuItemChangeVisibilityComponent,
    OverviewHeaderColumnChipsComponent,
    OverviewHeaderFilterChipsComponent,
    OverviewHeaderSearchComponent ],
  providers: [
    provideTranslationScope(TRANSLATION_SCOPE),
    provideTableConfigService(TRANSLATION_SCOPE, PROJECT_SPECIAL_TIMES_TABLE_CONFIG),
    TableDataService<ProjectCustomerDocumentDto>
  ],
  templateUrl: './project-documents-overview.component.html',
  styleUrl: './project-documents-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDocumentsComponent {
  private readonly _projectCustomerDocumentService: ProjectCustomerDocumentService = inject(ProjectCustomerDocumentService);

  private readonly _tableConfigService: TableConfigService = inject(TableConfigService);

  private readonly _tableDataService: TableDataService<ProjectCustomerDocumentDto> = inject(TableDataService<ProjectCustomerDocumentDto>, { self: true });

  private readonly _projectNumber: string = inject(PROJECT_NUMBER);

  protected readonly tableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);

  protected readonly tableData: Signal<ProjectCustomerDocument[]> = toSignal(this._tableDataService.data$
    .pipe(
      map((data: EntityState<ProjectCustomerDocumentDto>) => data.entities
        .map((documentDto: ProjectCustomerDocumentDto): ProjectCustomerDocument => parseToProjectCustomerDocument(documentDto)))
    ), { initialValue: [] });

  protected readonly isMenuExpanded: ModelSignal<boolean> = model(false);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected selectedDocument: WritableSignal<ProjectCustomerDocument | undefined> = signal(undefined);

  protected getDataForField: typeof getDataForField = getDataForField;

  constructor () {
    this._tableDataService.setConfig({
      type: TABLE_DATA_API_CONFIG_TYPES.preDefined,
      url: `api/projects/${ this._projectNumber }/customer-documents`
    });
  }

  protected addCustomerDocument (): void {
    this.selectedDocument.set(undefined);
    this.isMenuExpanded.set(true);
  }


  protected editDocument (projectCustomerDocument: ProjectCustomerDocument): void {
    this.selectedDocument.set(projectCustomerDocument);
    this.isMenuExpanded.set(true);
  }

  protected addDocument (documentId: ProjectCustomerDocumentId): void {
    this._projectCustomerDocumentService.addCustomerDocumentToProject(this._projectNumber, documentId)
      .subscribe({
        next: () => {
          this.isMenuExpanded.set(false);
          this._tableDataService.reload();
        }
      });
  }

  protected deleteDocument (documentId: ProjectCustomerDocumentId): void {
    this._projectCustomerDocumentService.deleteCustomerDocumentFromProject(this._projectNumber, documentId)
      .subscribe({
        next: () => {
          this.isMenuExpanded.set(false);
          this._tableDataService.reload();
        }
      });
  }
}

